import React, { useEffect, useLayoutEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  X, Send, Sparkles, AlertCircle, FileText, ShieldCheck,
  Bell, Loader2, Quote,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "./ui/button";
import { ScrollPanel } from "./ui/scroll-panel";
import { api, API, WS_STORAGE_KEY, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";

// ---------------------------------------------------------------------------
// SSE helper — parses one "event: X\ndata: Y" block (fetch() + native
// ReadableStream; no EventSource since it can't do POST with credentials).
// Deliberately duplicated from Chat.jsx rather than shared, so this change
// can't affect the already-shipped /chat page.
// ---------------------------------------------------------------------------
function parseSSEEvent(rawEvent) {
  let eventName = "message";
  const dataLines = [];
  for (const line of rawEvent.split("\n")) {
    if (line.startsWith("event:")) eventName = line.slice(6).trim();
    else if (line.startsWith("data:")) dataLines.push(line.slice(5).trim());
  }
  const raw = dataLines.join("\n");
  if (!raw) return null;
  try {
    return { event: eventName, data: JSON.parse(raw) };
  } catch (_) {
    return null;
  }
}

export default function ChatPanel({ documentId, documentType, cachedSuggestions, onCitationJump, onClose }) {
  const { t, i18n } = useTranslation("chat");
  const navigate = useNavigate();
  const [thread, setThread] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  // Guardian C2 — consume any sessionStorage prefill seeded by "Fix now" at mount.
  const [draft, setDraft] = useState(() => {
    if (typeof window === "undefined" || !documentId) return "";
    try {
      const key = `lg_chat_prefill_${documentId}`;
      const raw = window.sessionStorage.getItem(key);
      if (!raw) return "";
      const parsed = JSON.parse(raw);
      window.sessionStorage.removeItem(key);
      return parsed?.question || "";
    } catch {
      return "";
    }
  });
  const [usage, setUsage] = useState(null);
  const [streamingText, setStreamingText] = useState("");
  const scrollerRef = useRef(null);
  const bottomRef = useRef(null);

  // iter74 — Suggestions API. Prefer server-side deterministic response
  // (locale-aware fallback + doc-type defaults) over the client-side static
  // list. Uses `cachedSuggestions` prop as an immediate render hint while
  // the API round-trip finishes, then swaps in the fresh values.
  const staticFallback = t(`suggestions.${documentType || "lease"}`, {
    returnObjects: true, defaultValue: t("suggestions.lease", { returnObjects: true }),
  });
  const [dynamicSuggestions, setDynamicSuggestions] = useState(
    Array.isArray(cachedSuggestions) && cachedSuggestions.length >= 1
      ? cachedSuggestions
      : (Array.isArray(staticFallback) ? staticFallback : []),
  );
  const [suggestionsSource, setSuggestionsSource] = useState(
    Array.isArray(cachedSuggestions) && cachedSuggestions.length >= 1
      ? "cached" : "fallback",
  );
  const [suggestionsRefreshing, setSuggestionsRefreshing] = useState(false);
  const suggestions = dynamicSuggestions;

  useEffect(() => {
    let cancelled = false;
    if (!documentId) return;
    (async () => {
      try {
        const r = await api.get("/chat/suggestions", {
          params: { document_id: documentId, locale: i18n.language },
        });
        if (cancelled) return;
        if (Array.isArray(r.data?.questions) && r.data.questions.length >= 1) {
          setDynamicSuggestions(r.data.questions);
          setSuggestionsSource(r.data.source || "cached");
        }
      } catch {
        // Silent — keep the static/cached fallback
      }
    })();
    return () => { cancelled = true; };
  }, [documentId, i18n.language]);

  const refreshSuggestions = async () => {
    if (!documentId || suggestionsRefreshing) return;
    setSuggestionsRefreshing(true);
    try {
      const r = await api.post("/chat/suggestions/refresh", null, {
        params: { document_id: documentId, locale: i18n.language },
      });
      if (Array.isArray(r.data?.questions) && r.data.questions.length >= 1) {
        setDynamicSuggestions(r.data.questions);
        setSuggestionsSource(r.data.source || "llm");
        toast.success(t("suggestions_ui.refresh_success"));
      }
    } catch {
      toast.error(t("suggestions_ui.refresh_error"));
    } finally {
      setSuggestionsRefreshing(false);
    }
  };

  const loadThread = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const r = await api.get("/chat/threads", { params: { document_id: documentId } });
      setThread(r.data);
      setUsage(r.data.usage);
    } catch (e) {
      toast.error(e.response?.data?.detail || t("panel.load_failed"));
    } finally {
      if (!silent) setLoading(false);
    }
  }, [documentId, t]);

  useEffect(() => { loadThread(); }, [loadThread]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // A sentinel at the end of the list + scrollIntoView, rather than manually
  // computing scrollTop/scrollHeight — during token-by-token streaming the
  // latter can read stale layout numbers and fall behind. useLayoutEffect
  // (not useEffect) so this runs synchronously right after the DOM commit,
  // before the browser paints.
  useLayoutEffect(() => {
    bottomRef.current?.scrollIntoView({ block: "end", behavior: "auto" });
  }, [thread?.messages?.length, sending, streamingText]);

  // Streams the answer via SSE. Uses fetch() + a raw ReadableStream reader
  // instead of EventSource because EventSource can't send credentials/custom
  // headers on a POST. Auth still travels via the httpOnly cookie —
  // X-Workspace-Id is added by hand since this bypasses the axios
  // interceptor. On "done", silently refetch the thread so the streamed
  // preview is swapped for the authoritative persisted message (with
  // verified citations/confidence/out_of_scope) instead of reconstructing it
  // by hand on the client.
  const send = async (text) => {
    const q = (text ?? draft).trim();
    if (!q || sending || !thread) return;
    setSending(true);
    setDraft("");
    setStreamingText("");

    let response;
    try {
      const wsid = localStorage.getItem(WS_STORAGE_KEY);
      response = await fetch(`${API}/chat/threads/${thread.id}/messages/stream`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...(wsid ? { "X-Workspace-Id": wsid } : {}),
        },
        body: JSON.stringify({ question: q }),
      });
    } catch (e) {
      toast.error(e?.message || t("panel.send_failed"));
      setSending(false);
      setStreamingText("");
      return;
    }

    if (!response.ok || !response.body) {
      let detail = response.statusText;
      try {
        const errJson = await response.json();
        detail = formatApiErrorDetail(errJson?.detail) || detail;
      } catch (_) { /* body wasn't JSON — keep statusText */ }
      toast.error(detail || t("panel.send_failed"));
      setSending(false);
      setStreamingText("");
      return;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    try {
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let sepIndex;
        while ((sepIndex = buffer.indexOf("\n\n")) !== -1) {
          const rawEvent = buffer.slice(0, sepIndex);
          buffer = buffer.slice(sepIndex + 2);
          const evt = parseSSEEvent(rawEvent);
          if (!evt) continue;
          if (evt.event === "delta") {
            setStreamingText((prev) => prev + (evt.data.text || ""));
          } else if (evt.event === "done") {
            await loadThread({ silent: true });
          } else if (evt.event === "error") {
            toast.error(`${t("panel.send_failed")} — ${evt.data.detail || "stream error"}`);
            await loadThread({ silent: true });
          }
        }
      }
    } catch (e) {
      toast.error(e?.message || t("panel.send_failed"));
    } finally {
      setSending(false);
      setStreamingText("");
    }
  };

  const onComposerKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const overCap = usage && usage.daily_pct_used >= 100;
  const reachedQuestionLimit = thread && thread.questions_used >= thread.max_questions;
  const locale = i18n.language?.split("-")[0] === "fr" ? "fr" : "en";

  return (
    <div className="fixed inset-0 z-[60]" data-testid="chat-panel-root">
      <div className="absolute inset-0 bg-zinc-950/30 backdrop-blur-sm" onClick={onClose} data-testid="chat-panel-backdrop" />
      <aside
        className="absolute right-0 top-0 bottom-0 w-full sm:w-[520px] bg-white shadow-2xl flex flex-col animate-slide-in-right"
        data-testid="chat-panel"
      >
        <header className="px-5 py-4 border-b border-zinc-200 flex items-start justify-between gap-3 shrink-0">
          <div className="flex items-start gap-2 min-w-0">
            <div className="w-7 h-7 rounded-md grid place-items-center bg-zinc-950 text-white shrink-0">
              <Sparkles size={13} strokeWidth={2} />
            </div>
            <div className="min-w-0">
              <h2 className="font-display font-semibold text-base tracking-tight" data-testid="chat-panel-title">
                {t("panel.title")}
              </h2>
              <p className="text-[11px] text-zinc-500 leading-snug mt-0.5" data-testid="chat-banner-grounded">
                {t("panel.banner")}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-zinc-100 shrink-0" data-testid="chat-panel-close">
            <X size={18} />
          </button>
        </header>

        <ScrollPanel ref={scrollerRef} className="px-5 py-4 space-y-3" data-testid="chat-scroller">
          {loading ? (
            <div className="space-y-2">
              <div className="h-16 bg-zinc-50 animate-pulse rounded-2xl" />
              <div className="h-12 bg-zinc-50 animate-pulse rounded-2xl ml-auto w-3/4" />
            </div>
          ) : !thread?.messages?.length ? (
            <EmptyState
              suggestions={suggestions}
              source={suggestionsSource}
              refreshing={suggestionsRefreshing}
              onPick={(q) => send(q)}
              onRefresh={refreshSuggestions}
              disabled={sending}
              t={t}
            />
          ) : (
            thread.messages.map((m) =>
              m.role === "user"
                ? <UserBubble key={m.id} msg={m} locale={locale} />
                : <AssistantBubble key={m.id} msg={m} documentId={documentId} navigate={navigate}
                                   onClose={onClose} onCitationJump={onCitationJump} t={t} locale={locale} />
            )
          )}
          {sending && (streamingText
            ? <StreamingBubble text={streamingText} />
            : <ThinkingBubble t={t} />)}
          <div ref={bottomRef} />
        </ScrollPanel>

        <TokenMeter usage={usage} questionsUsed={thread?.questions_used || 0}
                    maxQuestions={thread?.max_questions || 30} t={t} />

        <div className="px-4 py-3 border-t border-zinc-200 shrink-0">
          {overCap ? (
            <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2" data-testid="chat-over-cap">
              {t("panel.over_cap")}
            </div>
          ) : reachedQuestionLimit ? (
            <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2" data-testid="chat-thread-cap">
              {t("panel.thread_cap", { max: thread.max_questions })}
            </div>
          ) : (
            <div className="flex items-end gap-2">
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={onComposerKey}
                placeholder={t("panel.composer_placeholder")}
                rows={2}
                className="flex-1 text-sm border border-zinc-200 rounded-lg px-3 py-2 focus:outline-none focus:border-zinc-950 resize-none min-h-[40px] max-h-32"
                data-testid="chat-composer-input"
                disabled={sending}
              />
              <Button
                onClick={() => send()}
                disabled={sending || !draft.trim()}
                className="bg-zinc-950 hover:bg-zinc-800 text-white h-10 px-3 self-stretch"
                data-testid="chat-composer-submit"
              >
                {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              </Button>
            </div>
          )}
          <p className="text-[10px] text-zinc-400 mt-1.5">{t("panel.composer_hint")}</p>
        </div>
      </aside>
    </div>
  );
}

// -- Empty state ------------------------------------------------------------
function EmptyState({ suggestions, source, refreshing, onPick, onRefresh, disabled, t }) {
  const sourceLabel = source && source !== "cached" ? t(`suggestions_ui.source_${source}`, "") : "";
  return (
    <div className="text-center py-8 px-4" data-testid="chat-empty-state">
      <div className="w-12 h-12 mx-auto rounded-full grid place-items-center bg-zinc-50 mb-3">
        <Sparkles size={20} className="text-zinc-700" strokeWidth={1.5} />
      </div>
      <p className="text-sm text-zinc-700 font-medium mb-1">{t("panel.empty_headline")}</p>
      <p className="text-xs text-zinc-500 mb-3">{t("panel.empty_subline")}</p>

      <div className="flex items-center justify-center gap-3 mb-4">
        <span
          className="text-[10px] uppercase tracking-[0.14em] text-zinc-400"
          data-testid="chat-suggestions-source"
        >
          {t("suggestions_ui.header")}{sourceLabel ? ` · ${sourceLabel}` : ""}
        </span>
        <button
          type="button"
          onClick={onRefresh}
          disabled={refreshing || disabled}
          className="text-[10px] uppercase tracking-[0.14em] text-zinc-500 hover:text-zinc-950 disabled:opacity-40 inline-flex items-center gap-1"
          data-testid="chat-suggestions-refresh"
        >
          {refreshing
            ? <Loader2 size={10} className="animate-spin" />
            : <Sparkles size={10} />}
          {t("suggestions_ui.refresh")}
        </button>
      </div>

      <div className="space-y-2 max-w-sm mx-auto" data-testid="chat-suggested-questions-list">
        {suggestions.map((q, i) => (
          <button
            key={i}
            onClick={() => onPick(q)}
            disabled={disabled}
            className="w-full text-left text-sm px-3 py-2 rounded-lg border border-zinc-200 bg-white hover:border-zinc-400 hover:bg-zinc-50 transition-colors disabled:opacity-50 inline-flex items-center gap-2"
            data-testid={`chat-suggested-question-${i}`}
          >
            <Sparkles size={11} className="text-zinc-400 shrink-0" />
            <span className="text-zinc-800">{q}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

// -- Bubbles ----------------------------------------------------------------
function UserBubble({ msg, locale }) {
  return (
    <div className="flex justify-end" data-testid={`chat-msg-user-${msg.id}`}>
      <div className="max-w-[78%]">
        <div className="bg-zinc-950 text-white text-sm rounded-2xl rounded-tr-sm px-4 py-2.5 whitespace-pre-wrap">
          {msg.content}
        </div>
        <p className="text-[10px] text-zinc-400 text-right mt-1 font-mono">{fmtTime(msg.ts, locale)}</p>
      </div>
    </div>
  );
}

function ThinkingBubble({ t }) {
  return (
    <div className="flex justify-start" data-testid="chat-msg-thinking">
      <div className="bg-white border border-zinc-200 rounded-2xl rounded-tl-sm px-4 py-3 inline-flex items-center gap-2">
        <Loader2 size={14} className="animate-spin text-zinc-500" />
        <span className="text-sm text-zinc-500">{t("panel.thinking")}</span>
      </div>
    </div>
  );
}

function StreamingBubble({ text }) {
  return (
    <div className="flex justify-start" data-testid="chat-msg-streaming">
      <div className="max-w-[94%] w-full">
        <div className="bg-white border border-zinc-200 rounded-2xl rounded-tl-sm px-4 py-3">
          <p className="text-sm text-zinc-900 whitespace-pre-wrap leading-relaxed" data-testid="chat-msg-streaming-content">
            {text}
          </p>
        </div>
      </div>
    </div>
  );
}

function AssistantBubble({ msg, documentId, navigate, onClose, onCitationJump, t, locale }) {
  if (msg.out_of_scope) {
    return (
      <div className="flex justify-start" data-testid={`chat-msg-out-of-scope-${msg.id}`}>
        <div className="max-w-[92%] border border-amber-300 bg-amber-50/40 rounded-2xl rounded-tl-sm px-4 py-3">
          <div className="flex items-start gap-2">
            <AlertCircle size={14} className="text-amber-700 mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-medium text-amber-950">{t("out_of_scope.headline")}</p>
              <p className="text-sm text-zinc-800 mt-1 whitespace-pre-wrap">{msg.content}</p>
              {msg.out_of_scope_reason && (
                <p className="text-[11px] text-zinc-500 mt-2 italic">
                  {t("out_of_scope.reason_prefix")}{msg.out_of_scope_reason}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const conf = msg.confidence || "medium";
  const confTone = conf === "high" ? "bg-emerald-50 text-emerald-800 border-emerald-200"
                : conf === "low" ? "bg-rose-50 text-rose-800 border-rose-200"
                : "bg-amber-50 text-amber-800 border-amber-200";

  return (
    <div className="flex justify-start" data-testid={`chat-msg-assistant-${msg.id}`}>
      <div className="max-w-[94%] space-y-2 w-full">
        <div className="bg-white border border-zinc-200 rounded-2xl rounded-tl-sm px-4 py-3">
          <p className="text-sm text-zinc-900 whitespace-pre-wrap leading-relaxed" data-testid={`chat-msg-content-${msg.id}`}>
            {msg.content}
          </p>

          {msg.citations?.length > 0 && (
            <div className="mt-3 space-y-2" data-testid={`chat-citations-${msg.id}`}>
              {msg.citations.map((c, i) => (
                <CitationCard
                  key={i}
                  citation={c}
                  testId={`chat-citation-${msg.id}-${i}`}
                  onJump={onCitationJump
                    ? () => { onCitationJump(c); }
                    : null}
                  t={t}
                />
              ))}
            </div>
          )}

          <div className="flex items-center gap-2 mt-3 flex-wrap">
            <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${confTone}`}
                  data-testid={`chat-msg-confidence-${conf}`}>
              ✓ {t("confidence.label")} · {t(`confidence.${conf}`)}
            </span>
          </div>
        </div>

        <ActionCtas msg={msg} documentId={documentId} navigate={navigate} onClose={onClose} t={t} />

        <p className="text-[10px] text-zinc-400 font-mono">{fmtTime(msg.ts, locale)}</p>
      </div>
    </div>
  );
}

function CitationCard({ citation, testId, onJump, t }) {
  return (
    <div className="border-l-4 border-zinc-300 bg-zinc-50/60 rounded-r-md pl-3 pr-2 py-2" data-testid={testId}>
      <div className="flex items-center justify-between gap-2 mb-1">
        <div className="flex items-center gap-1.5 min-w-0">
          <Quote size={10} className="text-zinc-500 shrink-0" />
          <p className="text-[10px] uppercase tracking-wider text-zinc-600 font-medium truncate">
            {citation.section_id}
          </p>
        </div>
        {onJump && (
          <button
            onClick={onJump}
            className="inline-flex items-center gap-0.5 text-[10px] text-zinc-700 hover:text-zinc-950 hover:underline shrink-0"
            data-testid={`${testId}-jump`}
            title={t("citation.jump_title")}
          >
            {t("citation.jump")}
          </button>
        )}
      </div>
      <p className="text-[12px] font-mono text-zinc-800 leading-snug whitespace-pre-wrap" data-testid={`${testId}-quote`}>
        &quot;{citation.quote}&quot;
      </p>
    </div>
  );
}

// -- Action CTAs (Create Alert / Reminder / Decision) ----------------------
function ActionCtas({ msg, documentId, navigate, onClose, t }) {
  const [busy, setBusy] = useState(null);
  const createAlert = async (withDueDate) => {
    setBusy(withDueDate ? "reminder" : "alert");
    try {
      const dueAt = withDueDate
        ? new Date(Date.now() + 14 * 86400000).toISOString()
        : null;
      await api.post("/alm/alerts", {
        title: truncate(msg.content, 90) || t("ctas.follow_up_title"),
        summary: msg.content,
        severity: withDueDate ? "Medium" : "High",
        document_id: documentId,
        due_at: dueAt,
      });
      toast.success(withDueDate ? t("ctas.reminder_created") : t("ctas.alert_created"));
    } catch (e) {
      toast.error(e.response?.data?.detail || t("ctas.create_failed"));
    } finally {
      setBusy(null);
    }
  };
  const goToDecision = () => {
    onClose?.();
    setTimeout(() => {
      const el = document.querySelector('[data-testid="decision-launcher"]');
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 200);
  };
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={`chat-action-ctas-${msg.id}`}>
      <button
        onClick={() => createAlert(false)}
        disabled={busy !== null}
        className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md border border-zinc-200 bg-white hover:border-zinc-400 hover:bg-zinc-50"
        data-testid={`chat-cta-alert-${msg.id}`}
      >
        <Bell size={10} /> {busy === "alert" ? t("ctas.create_alert_busy") : t("ctas.create_alert")}
      </button>
      <button
        onClick={() => createAlert(true)}
        disabled={busy !== null}
        className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md border border-zinc-200 bg-white hover:border-zinc-400 hover:bg-zinc-50"
        data-testid={`chat-cta-reminder-${msg.id}`}
      >
        <FileText size={10} /> {busy === "reminder" ? t("ctas.create_reminder_busy") : t("ctas.create_reminder")}
      </button>
      <button
        onClick={goToDecision}
        className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md border border-zinc-200 bg-white hover:border-zinc-400 hover:bg-zinc-50"
        data-testid={`chat-cta-decision-${msg.id}`}
      >
        <ShieldCheck size={10} /> {t("ctas.generate_decision")}
      </button>
    </div>
  );
}

// -- Token meter (usage only — never $) ------------------------------------
function TokenMeter({ usage, questionsUsed, maxQuestions, t }) {
  if (!usage) return null;
  const pct = Math.max(0, Math.min(100, usage.daily_pct_used || 0));
  const tone = pct >= 90 ? "bg-rose-600" : pct >= 70 ? "bg-amber-500" : "bg-zinc-700";
  return (
    <div className="px-5 py-2 border-t border-zinc-100 bg-zinc-50/40 shrink-0" data-testid="chat-token-meter">
      <div className="flex items-center justify-between text-[11px] text-zinc-600 mb-1">
        <span>
          {t("meter.question")} <span className="font-mono tabular-nums" data-testid="chat-question-count">{questionsUsed}</span>
          <span className="text-zinc-400"> / {maxQuestions}</span>
          <span className="mx-1.5 text-zinc-300">·</span>
          {t("meter.workspace_usage")}
        </span>
        <span className="font-mono tabular-nums" data-testid="chat-usage-pct">{pct}%</span>
      </div>
      <div className="w-full h-1.5 rounded-full bg-zinc-200 overflow-hidden" data-testid="chat-usage-bar">
        <div className={`h-full ${tone} transition-all`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

// -- helpers ---------------------------------------------------------------
function fmtTime(iso, locale) {
  if (!iso) return "";
  try {
    const tag = locale === "fr" ? "fr-FR" : "en-US";
    return new Date(iso).toLocaleTimeString(tag, { hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
}

function truncate(s, n) {
  if (!s) return "";
  return s.length > n ? s.slice(0, n - 1) + "…" : s;
}
